<template>
  <div class="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-w-full">
    <i
      :class="[
        'fa fa-sync tw-text-lg',
        pushInProgress ? 'fa-spin' : 'tw-opacity-0'
      ]"
    />

    <div class="tw-flex tw-flex-col tw-gap-5">
      <TaskItem
        v-for="(task, index) in activeTasks"
        :key="task.id || task.name || index"
        :task="task"
        :crm="crm"
        :crm-id="task.crm_id"
        :users="users"
        :teams="teams"
        :can-edit="canEdit"
        @updated="updateTask"
      />
    </div>

    <button
      class="tw-border-none tw-bg-transparent"
      :disabled="!canEdit"
      @click="createTask"
    >
      <i class="fa fa-plus-circle tw-text-2xl" />
    </button>
  </div>
</template>


<script setup>
import { ref, toRef, computed, watch, defineProps, getCurrentInstance } from 'vue';

// Allows us to use $oruga
const { proxy } = getCurrentInstance();

import { v4 as uuidv4 } from 'uuid';
import { debounce } from 'lodash';

import { useAdvisorStore } from '@frontend/ui/stores/advisorStore.js';
const advisorStore = useAdvisorStore();

import TaskItem from '@features/tasks/components/TaskItem.vue';


// Props & State
// -------------------------------------------------------------------------------->

const props = defineProps({
  meeting: {
    type: Object,
    required: true,
  },
  crm: {
    type: String,
    default: null,
  },
  lastTaskPush: {
    type: Number,
    default: null,
  },
  canEdit: {
    type: Boolean,
    default: false,
  },
});

const tasks = ref([]);
const users = ref([]); // Users in the CRM org that can be assigned to tasks
const teams = ref([]); // Teams in Wealthbox
const pushInProgress = ref(false);


// Computed
// -------------------------------------------------------------------------------->

// A computed prop for non-deleted tasks
const activeTasks = computed(() => {
  return tasks.value.filter(task => !task.deleted);
});

const crm = computed(() => {
  return props.meeting?.crm_type || props.crm;
});


// Regular Methods
// -------------------------------------------------------------------------------->

const createTask = () => {
  const newTask = {
    id: uuidv4(),
    created: new Date().toISOString(),
    crm_sync: true,
  };
  tasks.value.push(newTask);
};

const updateTask = updated => {
  const index = tasks.value.findIndex(task => task.id === updated.id);

  if (index === -1) {
    const errorMessage = `Unable to update task ${updated.id}}. Please contact support if the issue persists.`;
    notify(errorMessage);
    return;
  }

  tasks.value.splice(index, 1, updated);
  debouncedSave();
};

// The purpose of this is to store the CRM ID after pushing
// Allows us to track what has been pushed
// Also enables us to support updates and deletes in the future
const updateAfterPush = data => {
  if (!crm.value) return;

  const updates = {};

  data.forEach(response => {
    if ([200, 201].includes(response.status) && response.id) {
      updates[response.id] = {
        crm_id: response.data?.id || response.data?.activity?.id,
        crm: crm.value,
      };
    }
  });

  console.log('updateAfterPush.updates', updates);

  const updatedTasks = tasks.value.map(task => {
    if (updates[task.id]) {
      return {
        ...task,
        ...updates[task.id],
      };
    }
    return task;
  });
  console.log('updateAfterPush.updatedTasks', updatedTasks);
  tasks.value = updatedTasks;
  saveMeetingTasks();
};

const notify = (message, variant='danger') => {
  proxy.$oruga.notification.open({
    message,
    variant,
    position: 'top',
    duration: 5000,
  });
};


// API Calls
// -------------------------------------------------------------------------------->

const getMeetingTasks = async id => {
  const res = await fetch(`/api/v1/meetings/${id}/tasks/`, {
    method: 'GET',
    headers: advisorStore.headers,
  });
  const data = await res.json();

  if (!res.ok) {
    const errorMessage = 'Unable to load tasks. Try refreshing the page. Please contact support if the issue persists.';
    notify(data?.message || errorMessage);
    return;
  }

  data.forEach(task => {
    if (!isUUID(task.id)) task.id = uuidv4();
    if (task.crm_sync === undefined) task.crm_sync = true;
    if (!task.assignees) task.assignees = [];
    if (!task.team) task.team = null;
  });

  tasks.value = data || [];
};

const isUUID = id => {
  if (!id || typeof id !== 'string') return false;
  if (id.length !== 36) return false;
  return true;
};

const saveMeetingTasks = async () => {
  const res = await fetch(`/api/v1/meetings/${props.meeting.id}/tasks/`, {
    method: 'POST',
    headers: advisorStore.headers,
    body: JSON.stringify({
      tasks: tasks.value,
    }),
  });
  const data = await res.json();

  if (!res.ok) {
    const errorMessage = 'Unable to save tasks. Please contact support if the issue persists.';
    notify(data?.message || errorMessage);
  }
};

const debouncedSave = debounce(saveMeetingTasks, 300);

const pushMeetingTasks = async () => {
  const selectedForPush = tasks.value.filter(task => task.crm_sync && !task.deleted);
  // console.log('pushMeetingTasks.selectedForPush', selectedForPush);

  if (selectedForPush.length === 0) {
    notify('No tasks selected for push', 'warning');
    return;
  }

  const tasksToPush = selectedForPush.filter(task => !task.crm_id);

  if (tasksToPush.length === 0) {
    const msg = [
      'Selected tasks have already been pushed to your CRM.',
      'Currently, tasks can only be pushed once.',
    ];
    notify(msg.join(' '), 'info');
    return;
  }

  if (tasksToPush.filter(task => !task.due_date).length > 0) {
    notify('All tasks must have a due date', 'warning');
    return;
  }

  const formatted = tasksToPush.map(task => {
    const t = { ...task };
    if (!t.assignees) t.assignees = [];

    if (t.assignee && t.assignees.length === 0) {
      t.assignees.push(t.assignee);
    }

    t.assignees = t.assignees.map(id => {
      return { id, name: '' };
    });

    return t;
  });

  pushInProgress.value = true;

  const res = await fetch(`/api/v1/crm/${crm.value?.toLowerCase()}/tasks/`, {
    method: 'POST',
    headers: advisorStore.headers,
    body: JSON.stringify({
      tasks: formatted,
      contactId: props.meeting.crm_connection_id,
    }),
  });
  pushInProgress.value = false;
  const data = await res.json();

  if (!res.ok) {
    const errorMessage = 'Unable to save tasks. Please try again or contact support.';
    notify(data?.message || errorMessage);
    return;
  }

  notify(`Tasks pushed to ${crm.value}`, 'success');
  updateAfterPush(data);
};

const getUsers = async () => {
  const res = await fetch(`/api/v1/crm/${crm.value?.toLowerCase()}/users/`, {
    method: 'GET',
    headers: advisorStore.headers,
  });
  const data = await res.json();

  users.value = [{ id: null, name: '' }, ...(data.users || [])];
};

// Wealthbox only
const getTeams = async () => {
  if (crm.value !== 'Wealthbox') {
    teams.value = [];
    return;
  }

  const res = await fetch(`/api/v1/crm/${crm.value?.toLowerCase()}/teams/`, {
    method: 'GET',
    headers: advisorStore.headers,
  });
  const data = await res.json();

  teams.value = [{ id: null, name: '' }, ...(data.teams || [])];
};


// Watchers
// -------------------------------------------------------------------------------->

watch(toRef(props, 'meeting'), newValue => {
  if (newValue?.id) {
    getMeetingTasks(newValue.id);
  }
}, { immediate: true });

watch(crm, newValue => {
  if (newValue) {
    getUsers();
    getTeams();
  }
}, { immediate: true });

watch(() => props.lastTaskPush, newValue => {
  pushMeetingTasks();
});

</script>


<style scoped>

</style>
